import dayjs from 'dayjs'
import { Lang } from 'quasar'
import { useI18n } from 'vue-i18n'
import { api } from 'boot/axios'
import logger from 'src/services/logger'
import { useDevice } from 'src/modules/core/composables/useDevice.js'

const langList = import.meta.glob('../../../../node_modules/quasar/lang/*.js')
export const DEFAULT_LANGUAGE = 'fr'

let appLocale = null
let appLanguage = ''

export function useUserLanguage() {
  const { locale } = useI18n()

  /**
   * Saves the language preference in local storage and API. Reload page or set app language based on given arguments
   */
  async function saveAppLanguage({ lang, reload, setAppLanguage }) {
    appLanguage = lang || DEFAULT_LANGUAGE

    localStorage.setItem('lang', appLanguage)
    try {
      // Backend stores locale on any request
      await api.head('/me', { params: { l: appLanguage } })
    } catch (error) {
      logger.error(error)
    }

    if (reload) {
      window.location.reload()

      return
    }

    if (setAppLanguage) {
      await setAppLocale(appLanguage)

      const { isNativeCallAvailable, callNative } = useDevice()
      if (isNativeCallAvailable()) {
        await callNative('setLanguage', JSON.stringify({ language: appLanguage }), null)
      }
    }
  }

  /**
   * Sets the application's language and updates related configurations such as locale, text direction,
   * internationalization settings.
   */
  async function setAppLocale(appLanguage) {
    appLocale = typeof Intl.Locale === 'function' ? new Intl.Locale(appLanguage) : { textInfo: { direction: appLanguage === 'ar' ? 'rtl' : 'ltr' } }
    const direction = appLocale.textInfo?.direction ?? (appLanguage === 'ar' ? 'rtl' : 'ltr')
    document.documentElement.setAttribute('dir', direction)

    dayjs.locale(appLocale)
    locale.value = appLanguage
    await loadI18nFiles(appLanguage)
  }

  /**
   * Asynchronously loads internationalization (i18n) files based on the provided application language.
   * Adjusts the language input to 'en-GB' if 'en' is passed.
   *
   * @param {string} appLanguage - The language code used to determine which i18n file to load.
   * @return {Promise<void>} A promise that resolves when the language file has been successfully loaded and set.
   */
  async function loadI18nFiles(appLanguage) {
    if (appLanguage === 'en') {
      appLanguage = 'en-GB'
    }

    langList[ `../../../../node_modules/quasar/lang/${ appLanguage }.js` ]().then(lang => {
      Lang.set(lang.default)
    })
  }

  return {
    locale,
    saveAppLanguage
  }
}
